import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import ResponsibleMaintenanceService from "../../../../services/web/responsible-maintenance-service.js";
import {Template} from "../../../../components/template/template.jsx";
import Button from "../../../../components/button/button.jsx";
import {FaEraser, FaFileExcel, FaSearch} from "react-icons/fa";
import {Card} from "../../../../components/card/card.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../../utils/aggrid-utils.js";
import SelectComponent from "../../../../components/select/select-component.jsx";
import StatusCellRenderer from "../components/status-cell-renderer.jsx";
import {useQuery} from "@tanstack/react-query";
import NocService from "../../../../services/web/noc-service.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {PermissionEnum} from "../../../../utils/enums/permission-enum.js";
import useHasPermission from "../../../../hooks/use-has-permission.js";
import {Modal} from "../../../../components/modal/modal.jsx";
import nocService from "../../../../services/web/noc-service.js";
import {useSearchParams} from "react-router";
import ShowHideColumnsGrid from "../../../../components/show-hide-columns-grid/show-hide-columns-grid.jsx";

const API_URL = import.meta.env.VITE_API_URL;
const API_ARCHIVER = import.meta.env.VITE_API_ARCHIVER;

const STORAGE_KEY = "noc-camera-filter";
const HIDDEN_COLUMNS_STORAGE = "noc-cameras-hidden-columns";

const NocCamerasList = () => {
    const gridRef = useRef(null);
    const eventSourceRef = useRef(null);

    const [inputs, setInputs] = useState({
        archiver: 1,
        code: "",
        capturePlace: "",
        source: "",
        status: null,
        driver: "",
        maintenanceGroup: "",
        smartName: "",
    });

    const [rowData, setRowData] = useState([]);

    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState([]);
    const [driverOptions, setDriverOptions] = useState([]);

    const { data: maintenanceGroupOptions } = useQuery({
        queryKey: ["maintenanceGroup"],
        queryFn: () => ResponsibleMaintenanceService.maintenanceGroupName(),
    });

    const statusOptions = [
        { value: "ONLINE", label: "Online" },
        { value: "OFFLINE", label: "Offline" },
        { value: "WARNING", label: "Alerta" }
    ]

    const [openLastPassage, setOpenLastPassage] = useState(false);
    const [lastPassageImage, setLastPassageImage] = useState(null);
    const [lastPassageLoading, setLastPassageLoading] = useState(false);
    const [lastPassageError, setLastPassageError] = useState(false);
    const [lastPassageTitle, setLastPassageTitle] = useState("");

    const navigate = navigateHook();
    const hasPermission = useHasPermission();
    const capturePlacePermission = hasPermission(PermissionEnum.CAPTURE_PLACE);

    const theme = useMemo(() => aggridUtils.generalTheme(), []);

    const { data: archivers } = useQuery({
        queryKey: ["archivers"],
        queryFn: () =>
            NocService.allArchivers().then((data) =>
                data.map((item) => ({
                    value: item.id,
                    label: String(item.text),
                }))
            ),
    });

    const { data: nocLatePassageTime } = useQuery({
        queryKey: ["noc-late-passage-time"],
        queryFn: () => NocService.lastPassageTime(),
    });

    const getManufacturer = (driver) => driver?.split("/")[0] ?? "";

    const openLanes = (id) => {
        let isBlank = true;
        const urlDev = `/lanes?code=${id}`;
        
        navigate(urlDev, isBlank);
    }

    const lastPassage = async (camera) => {
        setOpenLastPassage(true);
        setLastPassageTitle(camera.description);

        setLastPassageLoading(true);
        setLastPassageError(false);
        setLastPassageImage(null);

        try {
            const response = await nocService.lastPassageImage(camera.code);

            if (response?.latestPassageUrl) {
                setLastPassageImage(`${API_URL}/lanes/latest-passage/get?url=${response.latestPassageUrl}`);
            } else {
                setLastPassageError(true);
            }
        } catch (error) {
            console.error(error);
            setLastPassageError(true);
        } finally {
            setLastPassageLoading(false);
        }
    };

    const defaultColDef = useMemo(() => ({
        tooltipValueGetter: (params) => {
            if (params.value === null || params.value === undefined) return "";
            return String(params.value);
        },
        sortable: true,
        resizable: true,
    }), []);

    const getStatusPriority = (status) => {
        switch (status) {
            case "OFFLINE":
                return 1;

            case "WARNING":
            case "LANE_NOT_CONFIG":
                return 2;

            case "ONLINE":
                return 3;

            default:
                return 99;
        }
    };

    const baseColumns = [
        { headerName: "", field: "status", width: 40, cellClass: "!p-0 !flex justify-center items-center",
            cellRenderer: StatusCellRenderer,
            comparator: (_, __, nodeA, nodeB) => getStatusPriority(nodeA.data.status) - getStatusPriority(nodeB.data.status),
            tooltipValueGetter: (params) => {
                switch (params.value) {
                    case "ONLINE":
                        return "Online";

                    case "WARNING":
                    case "LANE_NOT_CONFIG":
                        return "Alerta";

                    case "OFFLINE":
                        return "Offline";

                    default:
                        return "";
                }
            }
        },
        { headerName: "ID", field: "code", width: 50, cellStyle: { textAlign: "center", hover: "underline" },
            cellRenderer: (params) => {
                if (capturePlacePermission) {
                    return (
                        <span
                            className="flex items-center justify-center cursor-pointer hover:underline w-full h-full"
                            onClick={() => openLanes(params.value)}
                        >
                            {params.value}
                        </span>
                    );
                }

                return <span className="flex items-center justify-center w-full h-full">{params.value}</span>
            }
        },
        { headerName: "Descrição da câmera", headerTooltip: "Descrição da câmera", field: "description", flex: 1 },
        { headerName: "Posição", field: "position", width: 120,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        { headerName: "Origem", field: "source", width: 120,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        { headerName: "Fabricante/Driver", field: "driver", width: 160,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        { headerName: "Responsável", field: "maintenanceGroup", width: 140,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        { headerName: "Nome do dispositivo", field: "smartName", width: 180,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        { headerName: "D/h Última passagem", field: "lastPassage", width: 160, sort: "desc", sortIndex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {

                const parse = (v) => {
                    if (!v) return null;

                    const [d, t = "00:00:00"] = v.split(" ");
                    const [day, month, year] = d.split("/");
                    return new Date(`${year}-${month}-${day}T${t}`).getTime();
                };

                const parsedA = parse(a);
                const parsedB = parse(b);

                return aggridUtils.nullLastComparator(parsedA, parsedB, nodeA, nodeB, isInverted);
            },
            cellRenderer: (params) => {
                if (!params.value) return null;

                return (
                    <span
                        className="flex items-center justify-center cursor-pointer hover:underline w-full h-full"
                        onClick={() => lastPassage(params.data)}
                    >
                        {params.value}
                    </span>
                );
            }
        },
        { headerName: "Detalhes", headerTooltip: "Detalhes", field: "details", flex: 1,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            },
            valueGetter: params => {
                const { observation, details } = params.data;
                let str = observation ?? "";

                if (details?.length) {
                    details.forEach(d => {
                        if (d) str += str ? `, ${d}` : d;
                    });
                }

                return str;
            },
            tooltipValueGetter: params => {
                const { observation, details } = params.data;
                let tooltipArray = [];

                if (observation) tooltipArray.push(observation);

                if (details?.length) {
                    details.forEach(d => {
                        if (!d) return;

                        if (d === "Passagens atrasadas") tooltipArray.push(`Passagem registrada com atraso maior que ${nocLatePassageTime}`);
                        else tooltipArray.push(d);
                    });
                }

                return tooltipArray.join(", ");
            }
        },
        { headerName: "Observações", field: "observation" }
    ];

    const buildColumnDefs = () => {
        const hiddenColumns = JSON.parse(localStorage.getItem(HIDDEN_COLUMNS_STORAGE)) ?? [];
        return baseColumns.map(column => ({...column, hide: hiddenColumns.includes(column.field)}));
    };

    const [columnDefs, setColumnDefs] = useState(buildColumnDefs);

    const showHideColumnDefs = useMemo(() => {
        return columnDefs.map(column => column.field === "status" ? {...column, headerName: "Status"} : column);
    }, [columnDefs]);

    const toggleColumn = (field) => {
        setColumnDefs(prev => prev.map(column => column.field === field ? {...column, hide: !column.hide} : column));
    };

    const onColumnVisible = ({source, column}) => {
        if (source === "uiColumnMoved") toggleColumn(column.colDef.field);
    };

    const connectSSE = useCallback(() => {
        if (eventSourceRef.current) eventSourceRef.current.close();
        const source = new EventSource(`${API_ARCHIVER}/${inputs.archiver}/sse/cameras/subscribe`);

        source.onmessage = e => {
            const data = JSON.parse(e.data);
            if (data.type === "noc") {

                const getOptions = (field) => {
                    return [
                        ...new Set(
                            data.value
                                .map(item => getManufacturer(item[field]))
                                .filter(Boolean)
                        )
                    ].map(item => ({
                        value: item,
                        label: item,
                    }));
                };

                setDriverOptions(getOptions("driver"));
                setRowData(data.value);
            }
        };

        source.onerror = (e) => console.error(e);

        eventSourceRef.current = source;
    }, [inputs.archiver]);

    useEffect(() => {
        connectSSE();
        return () => eventSourceRef.current?.close();
    }, [connectSSE]);

    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) return;

        const parsedFilters = JSON.parse(saved);
        setFilters(parsedFilters);

        const codeFilter = parsedFilters.find(f => f.field === "code");
        if (codeFilter?.value?.length) setInputs(prev => ({...prev, code: codeFilter.value.join(",")}));
    }, []);

    const applyFilter = useCallback(() => {
        let data = [...rowData];

        filters.forEach(f => {
            if (f.type === "in") {
                data = data.filter(row =>
                    f.value.includes(String(row[f.field]))
                );
            }

            if (f.type === "like") {
                data = data.filter(row =>
                    String(row[f.field] ?? "")
                        .toLowerCase()
                        .includes(f.value.toLowerCase())
                );
            }

            if (f.type === "equal") {
                data = data.filter(row => {
                    const value = f.field === "driver" ? getManufacturer(row.driver) : row[f.field];
                    return String(value ?? "") === String(f.value);
                });
            }
        });

        setFilteredData(data);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }, [rowData, filters]);

    useEffect(() => {
        applyFilter();
    }, [rowData, filters]);

    const handleSearch = () => {
        const newFilters = [];

        if (inputs.code) {
            const codes = inputs.code
                .split(",")
                .flatMap(part => {
                    part = part.trim();

                    if (part.includes("-")) {
                        const [start, end] = part.split("-").map(Number);

                        if (!isNaN(start) && !isNaN(end)) {
                            const range = [];
                            for (let i = start; i <= end; i++) {
                                range.push(String(i));
                            }
                            return range;
                        }
                    }

                    return part;
                });

            newFilters.push({ field: "code", type: "in", value: codes });
        }

        if (inputs.capturePlace)
            newFilters.push({
                field: "capturePlace",
                type: "like",
                value: inputs.capturePlace,
            });

        if (inputs.source)
            newFilters.push({
                field: "source",
                type: "like",
                value: inputs.source,
            });

        if (inputs.status)
            if (inputs.status === "WARNING") {
                newFilters.push({
                    field: "status",
                    type: "in",
                    value: ["WARNING", "LANE_NOT_CONFIG"],
                });
            } else {
                newFilters.push({
                    field: "status",
                    type: "equal",
                    value: inputs.status,
                });
            }

        if (inputs.driver)
            newFilters.push({
                field: "driver",
                type: "equal",
                value: inputs.driver,
            });

        if (inputs.maintenanceGroup)
            newFilters.push({
                field: "maintenanceGroup",
                type: "equal",
                value: inputs.maintenanceGroup,
            });

        if (inputs.smartName)
            newFilters.push({
                field: "smartName",
                type: "like",
                value: inputs.smartName,
            });

        setFilters(newFilters);
    };

    const clearFilter = () => {
        setInputs({
            archiver: 1,
            code: "",
            capturePlace: "",
            smartName: "",
            source: "",
            driver: "",
            status: null,
            maintenanceGroup: "",
        });

        setFilters([]);
        setFilteredData(rowData);
        localStorage.removeItem(STORAGE_KEY);
    };

    useEffect(() => {
        const handler = e => {
            if (e.key === "Enter") handleSearch();
            if (e.key === "Escape") clearFilter();
        };

        window.addEventListener("keyup", handler);
        return () => window.removeEventListener("keyup", handler);
    }, [inputs]);

    const counters = useMemo(() => {
        const total = filteredData.length;
        let online = 0,
            offline = 0,
            warning = 0;

        filteredData.forEach(c => {
            switch (c.status) {
                case "ONLINE":
                    online++;
                    break;

                case "OFFLINE":
                    offline++;
                    break;

                case "WARNING":
                case "LANE_NOT_CONFIG":
                    warning++;
                    break;
            }
        });

        return { total, online, offline, warning };
    }, [filteredData]);

    const exportCsv = () => {
        const headers = [
            "Status",
            "ID",
            "Descrição",
            "Posição",
            "Origem",
            "Fabricante/Driver",
            "Responsável",
            "Nome do dispositivo",
            "D/h Última passagem",
            "Detalhes",
            "Observações",
        ];

        const statusLabel = (status) => {
            switch (status) {
                case "ONLINE":
                    return "Online";

                case "WARNING":
                case "LANE_NOT_CONFIG":
                    return "Alerta";

                case "OFFLINE":
                    return "Offline";

                default:
                    return "";
            }
        }

        const rows = filteredData.map(r => [
            statusLabel(r.status),
            r.code,
            r.description,
            r.position,
            r.source,
            r.driver,
            r.maintenanceGroup,
            r.smartName,
            r.lastPassage,
            r.details,
            r.observation,
        ]);

        const csv = "\uFEFF" + [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "noc-camera.csv";
        link.click();
    };

    return (
        <Template>
            <Template.Header>
                <Template.Title>NOC Câmeras</Template.Title>
                <Button target="success" onClick={exportCsv}>
                    <FaFileExcel /> Exportar
                </Button>
            </Template.Header>

            <Template.Body>
                <Card>
                    <Card.Body>
                        <div className="grid grid-cols-12 gap-3 mb-3">
                            <SelectComponent
                                label="Archiver"
                                value={archivers?.find(a => a.value === inputs.archiver)}
                                options={archivers || []}
                                onChange={(selectedOption) =>
                                    setInputs((prev) => ({
                                        ...prev,
                                        archiver: selectedOption?.value ?? null,
                                    }))
                                }
                                isClearable={false}
                                classNameWrapper="col-span-1"
                            />

                            <InputForm
                                id={"code"}
                                label="ID da câmera"
                                value={inputs.code}
                                onChange={e =>
                                    setInputs({ ...inputs, code: e.target.value })
                                }
                                classNameWrapper="col-span-2"
                            />

                            <InputForm
                                id={"capturePlace"}
                                label="Ponto de coleta"
                                value={inputs.capturePlace}
                                onChange={e =>
                                    setInputs({
                                        ...inputs,
                                        capturePlace: e.target.value,
                                    })
                                }
                                classNameWrapper="col-span-3"
                            />

                            <InputForm
                                id={"source"}
                                label="IP de origem"
                                value={inputs.source}
                                onChange={e =>
                                    setInputs({
                                        ...inputs,
                                        source: e.target.value,
                                    })
                                }
                                classNameWrapper="col-span-3"
                            />

                            <SelectComponent
                                id={"status"}
                                label="Status"
                                value={statusOptions.find(option => option.value === inputs.status) || null}
                                options={statusOptions}
                                onChange={(selectedOption) =>
                                    setInputs((prev) => ({
                                        ...prev,
                                        status: selectedOption?.value ?? null,
                                    }))
                                }
                                classNameWrapper="col-span-3"
                            />
                        </div>

                        <div className="grid grid-cols-12 gap-3">
                            <SelectComponent
                                id={"driver"}
                                label="Fabricante/Driver"
                                value={driverOptions.find(option => option.value === getManufacturer(inputs.driver)) ?? null}
                                options={driverOptions}
                                onChange={(selectedOption) =>
                                    setInputs((prev) => ({
                                        ...prev,
                                        driver: selectedOption?.value ?? null,
                                    }))
                                }
                                classNameWrapper="col-span-4"
                            />

                            <SelectComponent
                                id={"maintenanceGroup"}
                                label="Responsável"
                                value={(maintenanceGroupOptions ?? []).find(option => option.value === inputs.maintenanceGroup) ?? null}
                                options={maintenanceGroupOptions ?? []}
                                onChange={(selectedOption) =>
                                    setInputs((prev) => ({
                                        ...prev,
                                        maintenanceGroup: selectedOption?.value ?? null,
                                    }))
                                }
                                classNameWrapper="col-span-4"
                            />

                            <div className="col-span-4 flex gap-4 items-end">
                                <InputForm
                                    id={"smartName"}
                                    label="Nome do dispositivo"
                                    value={inputs.smartName}
                                    onChange={e =>
                                        setInputs({
                                            ...inputs,
                                            smartName: e.target.value,
                                        })
                                    }
                                    classNameWrapper="flex-1"
                                />

                                <div className={"flex gap-2"}>
                                    <Button target="primary-outline" title={"Limpar"} onClick={clearFilter}>
                                        <FaEraser/>
                                    </Button>

                                    <Button target="primary" title={"Buscar"} onClick={handleSearch}>
                                        <FaSearch/>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card className="mt-2 h-[calc(100vh-175px)]">
                    <Card.Body className="flex flex-col gap-2 h-full">
                        <div className={"h-full table-contact-id"}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={filteredData}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                enableCellTextSelection={true}
                                ensureDomOrder={true}
                                onColumnVisible={onColumnVisible}
                                onGridReady={(params) => {
                                    params.api.applyColumnState({
                                        state: [
                                            { colId: "status", sort: "asc", sortIndex: 0 },
                                            { colId: "lastPassage", sort: "desc", sortIndex: 1 }
                                        ],
                                        defaultState: { sort: null }
                                    });
                                }}
                                suppressCellFocus
                                tooltipShowDelay={200}
                                gridOptions={{ localeText: aggridUtils.localization() }}
                                theme={theme}
                                animateRows={false}
                            />
                        </div>

                        <div className="flex justify-center items-center pt-0.5">
                            <div className={"flex gap-2"}>
                                <div className={"flex justify-center items-center"}>
                                    <span className={"font-semibold text-[12px] text-[#555] ml-1"}>
                                        Total: {counters.total}
                                    </span>
                                </div>

                                <span className={"text-gray-200"}>|</span>

                                <div className={"flex justify-center items-center"}>
                                    <StatusCellRenderer value={"ONLINE"} legend/>
                                    <span className={"font-semibold text-[12px] text-[#555] ml-1"}>
                                        Online: {counters.online}
                                    </span>
                                </div>

                                <span className={"text-gray-200"}>|</span>

                                <div className={"flex justify-center items-center"}>
                                    <StatusCellRenderer value={"OFFLINE"} legend/>
                                    <span className={"font-semibold text-[12px] text-[#555] ml-1"}>
                                        Offline: {counters.offline}
                                    </span>
                                </div>

                                <span className={"text-gray-200"}>|</span>

                                <div className={"flex justify-center items-center"}>
                                    <StatusCellRenderer value={"WARNING"} legend/>
                                    <span className={"font-semibold text-[12px] text-[#555] ml-1"}>
                                        Alerta: {counters.warning}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <ShowHideColumnsGrid
                            columnDefs={showHideColumnDefs}
                            toggleColumn={toggleColumn}
                            storageItem={HIDDEN_COLUMNS_STORAGE}
                            buttonClassName={'transform translate-y-2'}
                            menuClassName={'h-fit!'}
                        />
                    </Card.Body>
                </Card>

                <Modal open={openLastPassage} setOpen={setOpenLastPassage} title={`Última passagem - ${lastPassageTitle}`} className={"w-6/12"}>
                    <Modal.Body className="p-2 pt-0 flex justify-center items-center min-h-[300px]">

                        { lastPassageLoading && (
                            <div className="text-center text-sm text-gray-500">Carregando...</div>
                        )}

                        { !lastPassageLoading && lastPassageError && (
                            <img
                                src={`${API_URL}/public/assets/img/sentry.imgnotfound.large.748x480.png`}
                                alt="Erro"
                                className="max-w-full max-h-full object-contain"
                            />
                        )}

                        { !lastPassageLoading && lastPassageImage && (
                            <img
                                src={lastPassageImage}
                                alt="Última passagem"
                                onError={() => setLastPassageError(true)}
                                className="max-w-full max-h-full object-contain"
                            />
                        )}
                    </Modal.Body>
                </Modal>
            </Template.Body>
        </Template>
    );
};

export default NocCamerasList;